import create from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '../i18n/config';

const useLanguageStore = create(
    persist(
        (set, get) => ({
            currentLanguage: 'it',
            defaultLanguage: 'it', // Lingua di default dell'evento
            availableLanguages: [],
            isMultilingual: false,
            
            // Normalizza i codici lingua (es. "it-IT" -> "it")
            normalizeLanguageCode: (langCode) => {
                if (!langCode) return 'it';
                const baseLang = langCode.split('-')[0].toLowerCase();
                return baseLang;
            },
            
            // Imposta le lingue disponibili basate sui dati dell'evento
            setAvailableLanguages: (eventData) => {
                const { normalizeLanguageCode } = get();
                
                if (!eventData) {
                    set({ availableLanguages: ['it'], isMultilingual: false });
                    return;
                }
                
                const isMultilingual = eventData.multilingua || false;
                
                if (!isMultilingual) {
                    const defaultLang = normalizeLanguageCode(eventData.defaultLanguage || 'it');
                    set({ 
                        availableLanguages: [defaultLang], 
                        isMultilingual: false,
                        currentLanguage: defaultLang,
                        defaultLanguage: defaultLang
                    });
                    i18n.changeLanguage(defaultLang);
                    return;
                }
                
                // Se multilingua è true, costruisci la lista delle lingue disponibili
                const languages = [];
                const defaultLang = normalizeLanguageCode(eventData.defaultLanguage || 'it');
                languages.push(defaultLang);
                
                // Salva il defaultLanguage nello store
                set({ defaultLanguage: defaultLang });
                
                // Aggiungi le lingue aggiuntive
                if (eventData.additionalLanguages && Array.isArray(eventData.additionalLanguages)) {
                    eventData.additionalLanguages.forEach(lang => {
                        const normalized = normalizeLanguageCode(lang);
                        if (!languages.includes(normalized)) {
                            languages.push(normalized);
                        }
                    });
                }
                
                set({ 
                    availableLanguages: languages, 
                    isMultilingual: true 
                });
            },
            
            // Cambia la lingua selezionata
            setLanguage: (lang) => {
                const normalized = get().normalizeLanguageCode(lang);
                set({ currentLanguage: normalized });
                // Sincronizza con i18n
                i18n.changeLanguage(normalized);
                // Salva anche in localStorage per compatibilità
                localStorage.setItem('selectedLanguage', normalized);
            },
            
            // Inizializza la lingua basata sui dati dell'evento e localStorage
            initializeLanguage: (eventData) => {
                const { normalizeLanguageCode, setAvailableLanguages } = get();
                
                if (!eventData) {
                    return;
                }
                
                // Il defaultLanguage dell'evento ha PRIORITÀ ASSOLUTA
                // Se defaultLanguage è undefined/null/stringa vuota, usa 'it' come fallback
                const rawDefaultLang = eventData.defaultLanguage;
                const eventDefaultLang = rawDefaultLang 
                    ? normalizeLanguageCode(rawDefaultLang)
                    : normalizeLanguageCode('it');
                
                // Salva il defaultLanguage nello store
                set({ defaultLanguage: eventDefaultLang });
                
                // Prima imposta le lingue disponibili
                setAvailableLanguages(eventData);
                
                const isMultilingual = eventData?.multilingua || false;
                
                if (!isMultilingual) {
                    // Se non multilingua, usa SEMPRE il defaultLanguage dell'evento
                    get().setLanguage(eventDefaultLang);
                    return;
                }
                
                // Se multilingua, controlla se c'è una lingua salvata in localStorage
                // In Zustand 3.x, il persist salva lo stato direttamente come oggetto
                let savedLang = null;
                try {
                    const persistData = localStorage.getItem('language-storage');
                    if (persistData) {
                        const parsed = JSON.parse(persistData);
                        // In Zustand 3.x, lo stato può essere salvato direttamente o in una proprietà 'state'
                        savedLang = parsed?.state?.currentLanguage || parsed?.currentLanguage || null;
                    }
                } catch (e) {
                    // Se c'è un errore nel parsing, ignora
                    console.warn('Error reading language from localStorage:', e);
                }
                
                const languages = get().availableLanguages;
                
                // PRIORITÀ: defaultLanguage dell'evento ha sempre la priorità
                // Solo se la lingua salvata è valida E diversa dal default, allora mantienila
                // Altrimenti usa sempre il defaultLanguage dell'evento
                const isValidSavedLang = savedLang && languages.includes(savedLang);
                
                // Usa la lingua salvata solo se è valida ED è diversa dal default (utente ha fatto una scelta esplicita)
                // Altrimenti usa sempre il defaultLanguage dell'evento
                const initialLang = (isValidSavedLang && savedLang !== eventDefaultLang) 
                    ? savedLang 
                    : eventDefaultLang;
                
                // Aggiorna sempre la lingua per assicurarsi che sia allineata con il defaultLanguage dell'evento
                // Questo garantisce che anche se c'era un valore persistito errato, venga sovrascritto
                get().setLanguage(initialLang);
            },
        }),
        {
            name: 'language-storage', // Nome della chiave in localStorage
            partialize: (state) => ({ 
                currentLanguage: state.currentLanguage,
                // Non persistiamo availableLanguages e isMultilingual perché dipendono dall'evento
            }),
        }
    )
);

export default useLanguageStore;
